import {
  CheckIcon,
  InfoIcon,
  Note,
  Panel,
  SectionHead,
  StatCard,
  StatGrid,
} from "../../../shared/components";
import { useResource } from "../../../shared/hooks/useResource";
import {
  formatPaise,
  getPublicPlans,
  type PlanRow,
} from "../../../shared/api/billing";
import type { Lang } from "../../../shared/i18n";
import { ADMIN_COPY } from "./adminCopy";
import { periodLabel } from "../superadmin/billingCopy";

/**
 * What a school sees before an operator has put them on a plan.
 *
 * 🔴 THE SCREEN USED TO SAY "No subscription" AND NOTHING ELSE — and that string
 * is `superadmin/billingCopy.ts`'s, where it is one cell in an operator's table
 * of hundreds of schools and the reader is the person who can fix it. On the
 * school's own page it names a problem, offers nothing, and leaves the rest of
 * the page blank. §"Every data surface ships four states" asks for *"a sentence
 * plus one action — never blank white space"*.
 *
 * 🔴 IT SHOWS THE PRICE LIST AND DOES NOT OFFER TO SET A PLAN, WHICH IS THE
 * WHOLE DESIGN. `BillingService` keeps every subscription write operator-only
 * for a reason it states outright — *"a school changing its own plan is a
 * discount waiting to happen"* — and `POST /school/subscription/checkout` takes
 * no body at all, because the plan and the amount come from a subscription that
 * must already exist. So a "choose this plan" button would be a control whose
 * submit the server refuses, which is the one thing `RosterControls` says no
 * screen may draw. What a school needs here is not a picker: it is to know what
 * the plans ARE, what their own size would be quoted against, and who to ask.
 *
 * ⚠️ THE LIST IS THE PUBLISHED ONE, NOT THE CATALOGUE. `GET /plans` is the
 * marketing page's read and filters on `isPublic AND isActive`, because *"a
 * bespoke district rate is live, serving a real school, and must never appear on
 * a price list"*. A school admin sees exactly what a visitor to the site sees —
 * no new endpoint, and no new way for a rate to leak.
 *
 * ⚠️ ITS OWN REQUEST AND ITS OWN FOUR STATES. A deployment where no plan has
 * been published yet is a real state, and it must read as "nothing published"
 * rather than as this panel failing to load.
 */
export interface NoPlanYetProps {
  /** Children enrolled right now — what this school would be quoted against. */
  children0: number;
  lang?: Lang;
}

export function NoPlanYet({ children0, lang = "en" }: NoPlanYetProps) {
  const t = ADMIN_COPY[lang];
  const plans = useResource<PlanRow[]>(
    (signal) => getPublicPlans(signal),
    ["public-plans"],
    { isEmpty: (rows) => rows.length === 0 },
  );

  return (
    <div className="flex flex-col gap-4">
      {/*
        🔴 THE SENTENCE FIRST, AND IT SAYS WHAT HAPPENS NEXT RATHER THAN WHAT IS
        MISSING. "No subscription" describes a hole; this describes the process
        the product actually runs — the marketing site's own model is *"a school
        leaves its details and somebody rings back"*, and this page should not
        invent a second, self-service one that the API would refuse.
      */}
      <Note tone="neutral" icon={<InfoIcon size={16} />}>
        {t.planNoneTitle} {t.planNoneWhy}
      </Note>

      {/*
        ⚠️ THEIR OWN NUMBER, BESIDE THE PRICES. Every plan below is per child, so
        a head teacher reading a per-student rate immediately wants to multiply it
        by something — and the figure is already on this response (`places.used`,
        live enrolment). Making them go and count is how a quote gets guessed.
      */}
      <StatGrid>
        <StatCard
          label={t.planNoneChildren}
          value={children0}
          caption={t.planNoneChildrenWhy}
          icon={<CheckIcon size={18} />}
          tone="indigo"
        />
      </StatGrid>

      <SectionHead hint={t.planNoneListWhy}>{t.planNoneListTitle}</SectionHead>

      {plans.state === "error" ? (
        <Note tone="warning" icon={<InfoIcon size={16} />}>
          {t.planNoneListFailed}
        </Note>
      ) : plans.state === "empty" ? (
        /* ⚠️ NOTHING PUBLISHED IS NOT A FAILURE — it is a deployment whose
           operator has not marked a plan public yet, and it reads as itself. */
        <Note tone="neutral" icon={<InfoIcon size={16} />}>
          {t.planNoneListEmpty}
        </Note>
      ) : plans.data === null ? null : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {plans.data.map((plan) => (
            <Panel key={plan.id}>
              <div className="flex flex-col gap-1.5">
                <span className="font-dash text-dash-md font-extrabold text-ink">
                  {plan.name}
                </span>

                {/*
                  🔴 BOTH NUMBERS, BECAUSE A PER-CHILD RATE ALONE IS NOT THE
                  PRICE. `floorPaise` is a minimum charge — a small school pays
                  it however few children it has — and a card showing only the
                  per-student figure would quote a nursery of eight a number it
                  will never be billed.
                */}
                <span className="font-dash text-dash-lg font-extrabold text-ink tabular">
                  {formatPaise(plan.perStudentPaise, plan.currency)}
                </span>
                <span className="font-dash text-dash-xs text-muted">
                  {t.planNonePerChild(periodLabel(plan.period, lang))}
                </span>
                <span className="font-dash text-dash-xs text-muted tabular">
                  {t.planNoneFloor(formatPaise(plan.floorPaise, plan.currency))}
                </span>

                {plan.blurb ? (
                  <span className="mt-1 font-dash text-dash-xs text-muted">
                    {plan.blurb}
                  </span>
                ) : null}
              </div>
            </Panel>
          ))}
        </div>
      )}

      {/*
        🔴 THE ONE ACTION, AND IT IS THE ONE THE PRODUCT ACTUALLY SUPPORTS. There
        is no route by which a school starts its own subscription — that is
        `POST /billing/schools/:id/subscription`, `@Roles('SUPER_ADMIN')` — so
        the honest next step is the conversation the sales model is built on. A
        button that posted somewhere would be a promise nothing on the server
        keeps.
      */}
      <Note tone="neutral" icon={<InfoIcon size={16} />}>
        {t.planNoneAsk}
      </Note>
    </div>
  );
}
